import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Typography,
  message,
} from "antd";
import { KlearlyCountryAlpha2Code } from "../../interfaces/klearlyCountryAlpha2Code.enum";
import CountryFlag from "../common/country-flag/CountryFlag";
import { provisionSandboxPartner } from "./partnersSandboxApi";
import {
  IProvisionSandboxPartnerInput,
  IProvisionSandboxPartnerResult,
  IProvisionedCredential,
} from "./partnersSandbox.types";

const { Title, Text, Paragraph, Link } = Typography;

const formItemLayout = {
  labelCol: { span: 8 },
};

function CredentialCard({
  title,
  credential,
}: {
  title: string;
  credential: IProvisionedCredential;
}) {
  return (
    <Card title={title} size="small" style={{ width: 440 }}>
      <Paragraph copyable={{ text: credential.email }}>
        <Text type="secondary">Email: </Text>
        <Text strong>{credential.email}</Text>
      </Paragraph>
      <Paragraph copyable={{ text: credential.password }}>
        <Text type="secondary">Password: </Text>
        <Text strong>{credential.password}</Text>
      </Paragraph>
      <Paragraph style={{ marginBottom: 0 }}>
        <Text type="secondary">Hub: </Text>
        <Link href={credential.hubUrl} target="_blank" rel="noreferrer">
          {credential.hubUrl}
        </Link>
      </Paragraph>
    </Card>
  );
}

export default function PartnersSandboxForm() {
  const [form] = Form.useForm<IProvisionSandboxPartnerInput>();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IProvisionSandboxPartnerResult | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (values: IProvisionSandboxPartnerInput) => {
    setLoading(true);
    setError(null);
    try {
      const bundle = await provisionSandboxPartner(values);
      setResult(bundle);
      message.success("Sandbox partner and merchant created");
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create the sandbox partner";
      setError(msg);
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    form.resetFields();
  };

  if (result) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>Sandbox partner created</Title>
        <Paragraph type="secondary">
          {result.emailSent
            ? `We've emailed these login details to ${result.partner.email}. You can also share them directly below.`
            : "Share these login details with the partner."}
        </Paragraph>
        <Space direction="vertical" size="middle">
          <CredentialCard title="Partner Hub" credential={result.partner} />
          <CredentialCard title="Merchant Hub" credential={result.merchant} />
          <Button type="primary" onClick={reset}>
            Create another
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 560 }}>
      <Title level={4}>Partners Sandbox</Title>
      <Paragraph type="secondary">
        Create a fully onboarded sandbox partner plus a linked, payment-ready
        sandbox merchant so a partner can start testing right away.
      </Paragraph>
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form
        form={form}
        onFinish={onSubmit}
        autoComplete="off"
        initialValues={{ country: KlearlyCountryAlpha2Code.Netherlands }}
      >
        <Form.Item
          name="partnerName"
          label="Partner Name"
          labelCol={{ ...formItemLayout.labelCol }}
          labelAlign="left"
          rules={[{ required: true, message: "Missing Partner Name" }]}
        >
          <Input style={{ width: 240 }} />
        </Form.Item>
        <Form.Item
          name="ownerName"
          label="Owner Name"
          labelCol={{ ...formItemLayout.labelCol }}
          labelAlign="left"
          rules={[{ required: true, message: "Missing Owner Name" }]}
        >
          <Input style={{ width: 240 }} />
        </Form.Item>
        <Form.Item
          name="partnerEmail"
          label="Partner Email"
          labelCol={{ ...formItemLayout.labelCol }}
          labelAlign="left"
          rules={[
            { required: true, message: "Missing Partner Email" },
            { type: "email", message: "Not a valid email." },
          ]}
        >
          <Input style={{ width: 240 }} />
        </Form.Item>
        <Form.Item
          name="country"
          label="Country"
          labelCol={{ ...formItemLayout.labelCol }}
          labelAlign="left"
          rules={[{ required: true, message: "Missing Country" }]}
        >
          <Select showSearch optionFilterProp="label" style={{ width: 240 }}>
            {Object.entries(KlearlyCountryAlpha2Code).map(([name, code]) => (
              <Select.Option key={code} value={code} label={name}>
                <CountryFlag alpha2={code} style={{ marginRight: 6 }} />
                {name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={loading}>
            Create sandbox partner
          </Button>
        </Form.Item>
      </Form>
      <Paragraph type="secondary" style={{ marginTop: 8 }}>
        A <Text code>+merchant</Text> variant of the partner email is created
        automatically as the merchant login (shown after creation).
      </Paragraph>
    </div>
  );
}
